import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Archive, MapPin, FileText, Copy, Loader2, RefreshCw, Trash2, Eye, Edit2, Database } from 'lucide-react';
import { newslettersApi, NewsletterRecord, extractLookupDisplay, submissionsApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Newsletters() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [newsletters, setNewsletters] = useState<NewsletterRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fetchNewsletters = async () => {
    setLoading(true);
    try {
      const data = await newslettersApi.list();
      setNewsletters(data);
    } catch (err) {
      console.error('Failed to fetch newsletters:', err);
      toast.error('Failed to load newsletters');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchNewsletters();
  }, []);
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      // Check for linked submissions before deleting
      const newsletter = newsletters.find(n => n.ID === id);
      let submissionCount = 0;
      if (newsletter?.Newsletter_ID) {
        try {
          const linkedSubs = await submissionsApi.findByNewsletterId(newsletter.Newsletter_ID);
          submissionCount = linkedSubs.length;
        } catch {
          // Non-blocking — proceed with delete
        }
      }
      await newslettersApi.delete(id);
      if (submissionCount > 0) {
        toast.success('Newsletter deleted', {
          description: `Note: ${submissionCount} submission(s) still reference this newsletter's ID. They are preserved as historical records.`,
          duration: 8000
        });
      } else {
        toast.success('Newsletter deleted');
      }
      setDeleteConfirm(null);
      fetchNewsletters();
    } catch (err) {
      console.error('Failed to delete newsletter:', err);
      toast.error('Failed to delete newsletter');
    } finally {
      setIsDeleting(false);
    }
  };
  // Filter by team and region
  const filteredNewsletters = newsletters.filter(n => {
    // 1. Filter by Team
    if (n.Team !== currentWorkspace.name) return false;
    // 2. Filter by Region
    // If currentSubRegion is set, show records matching that region OR global records (empty region)
    // If currentSubRegion is NOT set (All Regions), show everything
    if (currentSubRegion) {
      return !n.Region || n.Region === currentSubRegion;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-5505" data-mp="cprifn15md2u">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5506" data-mp="qmn5ije8q51w">
        <div data-id="element-5507" data-mp="lolmtzkq87qy">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5508" data-mp="lov48yrph6l1">
            Newsletters & Lists
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5509" data-mp="15awuhwfy9aza">
            Manage audience lists and configuration for your campaigns.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5510" data-mp="vbplflg902hz">
          <button onClick={fetchNewsletters} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-5511" data-mp="vaw984yisksg">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5512" data-mp="micjju07hqjr" />
          </button>
          <Link to="/newsletters/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-5513" data-mp="rb5nwgrriwvb">
            <Plus className="h-4 w-4" data-id="element-5514" data-mp="hlcdvk79qpt6" />
            Create Newsletter
          </Link>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-5515" data-mp="3lt3sluwd4e8">
        {loading && newsletters.length === 0 ? <div className="p-12 text-center" data-id="element-5516" data-mp="7nxxfklci10v">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-5517" data-mp="j6mfbrfmbxa" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-5518" data-mp="3rphs5l7e00uy">Loading newsletters...</p>
          </div> : filteredNewsletters.length === 0 ? <div className="p-12 text-center" data-id="element-5519" data-mp="3mwwycjohdnrf">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-5520" data-mp="3hzeqyxq43glj" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5521" data-mp="3hz4x2z1y4pak">
              No newsletters found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-5522" data-mp="3dvgqojp1vzqw">
              Create your first newsletter list to start collecting subscribers.
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-5523" data-mp="341q27esxbpb7">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-5524" data-mp="2z12ass0s96gg">
              <thead className="bg-gray-50" data-id="element-5525" data-mp="2z2x0ejn01mqv">
                <tr data-id="element-5526" data-mp="1277ka8vlq5zs">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5527" data-mp="127quf3f0fq39">
                    Newsletter Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5528" data-mp="15p523r1ob9e0">
                    Signup Form
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5529" data-mp="1eirur8al3wiz">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5530" data-mp="1jgtbvnog9lrt">
                    Mailing Lists
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5531" data-mp="qtytqxf3uyhi">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5532" data-mp="10qts9gbdoyvs">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-5533" data-mp="2c60divj7arha">
                {filteredNewsletters.map(newsletter => <tr key={newsletter.ID} className="hover:bg-gray-50" data-id="element-5534" data-mp="1mh3730ieu1jk">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5535" data-mp="1hjjkndbk6igj">
                      <div className="text-sm font-medium text-gray-900" data-id="element-5536" data-mp="1hle7ix6e4kn9">
                        {newsletter.Newsletter}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5537" data-mp="1rhqyhnajwkeb">
                      {newsletter.Sign_Up_Form ? <span className="inline-flex items-center gap-1 text-sm text-gray-600" data-id="element-5538" data-mp="13piw1cebmbtw">
                          <FileText className="h-3.5 w-3.5" data-id="element-5539" data-mp="yqfpfniozxjt" />
                          {extractLookupDisplay(newsletter.Sign_Up_Form)}
                        </span> : <span className="text-sm text-gray-400" data-id="element-5540" data-mp="18pewj1nt7l4p">
                          No form linked
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5541" data-mp="g1j5ach0elfb">
                      {newsletter.Region ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5542" data-mp="2ro35m6fwspsk">
                          <MapPin className="h-3 w-3" data-id="element-5543" data-mp="2hrhvx7sy2qie" />
                          {newsletter.Region}
                        </span> : <span className="text-xs text-gray-500 italic" data-id="element-5544" data-mp="2mo8ec1s81y6z">
                          All Regions
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5545" data-mp="3fegq9bw5zmd4">
                      {newsletter.Mailing_Lists && newsletter.Mailing_Lists.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-5546" data-mp="35fhp0edqat05">
                          {newsletter.Mailing_Lists.slice(0, 2).map((l, i) => <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-5547" data-mp="1q2erns125g5r" data-mpi={i}>
                              <Database className="h-3 w-3" data-id="element-5548" data-mp="2gokjn28umrop" />
                              {l.Mailing_List_Name}
                            </span>)}
                          {newsletter.Mailing_Lists.length > 2 && <span className="text-xs text-gray-400" data-id="element-5549" data-mp="34fgigjbq2n5g">
                              +{newsletter.Mailing_Lists.length - 2} more
                            </span>}
                        </div> : <span className="text-sm text-gray-400" data-id="element-5550" data-mp="2vljzfddutqu9">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5551" data-mp="2qmh2yhbxwsxw">
                      {newsletter.Newsletter_Tags && newsletter.Newsletter_Tags.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-5552" data-mp="3jbnxz6c6r0ee">
                          {newsletter.Newsletter_Tags.slice(0, 3).map((t, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-5553" data-mp="3vnzyl11zxwgh" data-mpi={i}>
                                {t.Tag_Name}
                              </span>)}
                          {newsletter.Newsletter_Tags.length > 3 && <span className="text-xs text-gray-400" data-id="element-5554" data-mp="n755gdb03qfl">
                              +{newsletter.Newsletter_Tags.length - 3} more
                            </span>}
                        </div> : <span className="text-sm text-gray-400" data-id="element-5555" data-mp="117637jvyyhqu">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5556" data-mp="oonqad8k98g7">
                      <div className="flex items-center justify-end gap-2" data-id="element-5557" data-mp="tmp8kvy5x3qm">
                        <Link to={`/newsletters/${newsletter.ID}`} className="text-gray-400 hover:text-blue-600 p-2 hover:bg-blue-50 rounded" title="View Details" data-id="element-5558" data-mp="tkw3vm740tl9">
                          <Eye className="h-4 w-4" data-id="element-5559" data-mp="17e3vhdv6xp2y" />
                        </Link>
                        <Link to={`/newsletters/${newsletter.ID}/edit`} className="text-gray-400 hover:text-blue-600 p-2 hover:bg-blue-50 rounded" title="Edit" data-id="element-5560" data-mp="1ccf1fdid7ffw">
                          <Edit2 className="h-4 w-4" data-id="element-5561" data-mp="3j4fxqpch466p" />
                        </Link>
                        <button onClick={() => setDeleteConfirm(newsletter.ID)} className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-5562" data-mp="3o39b8hrrfx6v">
                          <Trash2 className="h-4 w-4" data-id="element-5563" data-mp="20g4tywkud8ix" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5564" data-mp="2o4o4lird5xe6">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5565" data-mp="2k6qca72aqv6k">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-5566" data-mp="2f5j4w942fy0f" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-5567" data-mp="2a9lqw9g9z3qt">
              <div className="flex items-center gap-4 mb-4" data-id="element-5568" data-mp="13p2n4951h5ae">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5569" data-mp="yr13nn3pb3ye">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-5570" data-mp="109re0txlgnfz" />
                </div>
                <div data-id="element-5571" data-mp="1579quveqfmt9">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-5572" data-mp="157sz1kekywu3">
                    Delete Newsletter
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-5573" data-mp="rawa5oi0vpfu">
                    Are you sure? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-5574" data-mp="hgng2wdcfofk">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5575" data-mp="m9qgnogjfy59">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-5576" data-mp="3ups8vrv9a8vx">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5577" data-mp="3b5ba2ay157bb" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}